import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import apiSlice from '@/store/api/apiSlice';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001';
const IS_ABSOLUTE = /^https?:\/\//i.test(API_BASE);

export function usePaymentSocket(token: string | null) {
  const socketRef = useRef<Socket | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!token) return;

    // Absolute base (dev / dedicated API host): connect straight to it.
    // Relative base (white-label prod, e.g. "/api"): connect same-origin and
    // route the handshake through the /api path Caddy proxies to the backend,
    // so the brand's Host header reaches the server as usual.
    const socket = IS_ABSOLUTE
      ? io(`${API_BASE}/payment`, {
          auth: { token }, transports: ['websocket'],
          reconnectionDelay: 3000, reconnectionAttempts: 10,
        })
      : io('/payment', {
          path: `${API_BASE}/socket.io`,
          auth: { token }, transports: ['websocket'],
          reconnectionDelay: 3000, reconnectionAttempts: 10,
        });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[PaymentSocket] Connected');
    });

    socket.on('request-updated', (data: {
      requestId: string;
      status: 'Approved' | 'Rejected';
      amount: number;
      type: string;
      remark?: string;
    }) => {
      // Refresh deposit/withdrawal history so the UI updates
      dispatch(apiSlice.util.invalidateTags(['Account']));

      const label = data.type === 'Credit' ? 'Deposit' : 'Withdrawal';
      const amount = `₹${Number(data.amount).toLocaleString('en-IN')}`;

      if (data.status === 'Approved') {
        toast.success(`${label} request of ${amount} has been Approved!`, {
          duration: 6000,
          style: {
            background: '#f0fdf4',
            color: '#166534',
            border: '1px solid #bbf7d0',
            fontWeight: '600',
          },
        });
      } else {
        toast.error(
          `${label} request of ${amount} has been Rejected${data.remark ? `: ${data.remark}` : ''}`,
          {
            duration: 8000,
            style: {
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              fontWeight: '600',
            },
          }
        );
      }
    });

    socket.on('disconnect', () => {
      console.log('[PaymentSocket] Disconnected');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, dispatch]);

  return socketRef;
}
